import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { Grid, Paper, Container, Divider, Box } from '@material-ui/core'
import messages from '@utils/messages'
import cooAndCoaMessages from '@utils/MessageSets/defineCooAndCoaMessages'
import { CertificateText } from '@src/types/graphql-types'
import HandleMarkup from '@src/components/HandleMarkup'
import CertificateHeader from './CertificateHeader'

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: '3rem',
  },
  certificateData: {
    padding: '1.5rem 0',
    maxWidth: '100%',
  },
  headingRow: {
    margin: '1rem 0',
  },
  noprint: {},
  ['@media print']: {
    noprint: {
      display: 'none',
    },
  },
  tableHeading: {
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightMedium,
    textTransform: 'uppercase',
  },
  dataRow: {
    fontSize: theme.typography.pxToRem(12),
    padding: '0px !important',
  },
  rowDivider: {
    margin: `${theme.typography.pxToRem(2)} 0`,
    width: '100%',
  },
  signatureImg: {
    maxHeight: '100px',
    maxWidth: '100%',
  },
}))

const keyMap = {
  name: 'PRODUCT_NAME',
  description: 'PRODUCT_DESCRIPTION',
  productNumber: 'PRODUCT_NUMBER',
  brand: 'PRODUCT_BRAND',
  casNumber: 'CAS_NUMBER',
  linearFormula: 'MOLECULAR_FORMULA',
  molecularWeight: 'MOLECULAR_WEIGHT',
}

interface CertificateSharedLayoutProps {
  certificateData: CertificateText
  gridSpec: 4 | 6
  coa?: boolean
}

const CertificateSharedLayout: React.FC<CertificateSharedLayoutProps> = ({
  certificateData,
  gridSpec,
  coa,
}) => {
  const classes = useStyles()
  const { formatMessage } = useIntl()
  const { product } = certificateData
  const testSpecs = certificateData.testSpecifications
  const revisionData = {
    revno: 'REVNO',
    date: 'FORMATTED_MODIFIEDDATE',
    ifNewThenYes: 'IFNEWTHENYES',
  }
  const filteredTestSpecs =
    testSpecs &&
    testSpecs.filter(
      (spec) =>
        spec.name !== revisionData.date &&
        spec.name !== revisionData.revno &&
        spec.name !== revisionData.ifNewThenYes
    )
  React.useEffect(() => {
    const headerElement = document.getElementsByTagName('header')[0]
    headerElement.classList.add(classes.noprint)
    const footerElement = document.getElementById('footer')
    footerElement && footerElement.classList.add(classes.noprint)
    setTimeout(function () {
      const globalMessageElement = document.querySelector(
        '[data-testid="sitewide-messages"]'
      )
      globalMessageElement &&
        globalMessageElement.classList.add(classes.noprint)
    }, 1000)
  })

  const productKeys = product
    ? Object.keys(product).filter((key) => key !== '__typename')
    : null
  return (
    <Container maxWidth="md" className={classes.container}>
      <CertificateHeader
        coa={coa}
        testSpecs={testSpecs}
        revisionData={revisionData}
      />
      {/* Table Body */}
      <Paper elevation={0} square className={classes.certificateData}>
        <Grid
          container
          alignItems="center"
          spacing={2}
          style={{ marginBottom: 5 }}
        >
          {coa &&
            product &&
            productKeys &&
            productKeys.map((key) => {
              const productLabel = keyMap[key]
              return (
                product[key] && (
                  <Grid container className={classes.dataRow} key={key}>
                    <Grid item xs={6}>
                      <strong>
                        <FormattedMessage {...messages[productLabel]} />:
                      </strong>
                    </Grid>
                    <Grid item xs={6}>
                      {key === 'brand' ? (
                        <HandleMarkup value={product[key].name} />
                      ) : (
                        <HandleMarkup value={product[key]} />
                      )}
                    </Grid>
                    <Divider className={classes.rowDivider} />
                  </Grid>
                )
              )
            })}
        </Grid>
        {coa && filteredTestSpecs && (
          <Grid item container className={classes.headingRow}>
            <Grid item xs={4}>
              <Box className={classes.tableHeading}>
                <FormattedMessage {...messages.TEST} />
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box className={classes.tableHeading}>
                <FormattedMessage {...messages.SPECIFICATION} />
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box className={classes.tableHeading}>
                <FormattedMessage
                  {...messages.LOT_NUMBER_RESULTS}
                  values={{ lotNumber: certificateData.lotNumber }}
                />
              </Box>
            </Grid>
          </Grid>
        )}
        <Grid
          container
          alignItems="center"
          spacing={2}
          style={{ margin: '0 !important' }}
        >
          {filteredTestSpecs &&
            filteredTestSpecs.map((testSpec, index) => {
              const { name, value, result } = testSpec
              const key = name.replace(' ', '_').toUpperCase()
              const coaMessage = cooAndCoaMessages[`COA_${key}`]

              const label = coaMessage ? formatMessage(coaMessage) : name
              const clickHereHref = result?.match(
                /href=([^"]+?\.(?:pdf))>/i
              )?.[1]
              return value ? (
                <Grid key={index} item container className={classes.dataRow}>
                  <Grid item xs={gridSpec}>
                    <strong>
                      <HandleMarkup value={label} />
                    </strong>
                  </Grid>
                  <Grid item xs={gridSpec}>
                    <HandleMarkup value={value} />
                  </Grid>
                  {coa && result && (
                    <Grid item xs={4}>
                      {clickHereHref ? (
                        <FormattedMessage
                          id="COA_PDF_RESULTS"
                          defaultMessage="Please <a>click here</a> to obtain the Certificate of Analysis in PDF format."
                          values={{
                            a: (...chunks) => (
                              <a href={clickHereHref}>{chunks}</a>
                            ),
                          }}
                        />
                      ) : (
                        <HandleMarkup value={result} />
                      )}
                    </Grid>
                  )}
                  <Divider className={classes.rowDivider} />
                </Grid>
              ) : null
            })}
          <div className={classes.dataRow}>
            {certificateData.signatureUrl ? (
              <img
                src={certificateData.signatureUrl}
                className={classes.signatureImg}
                alt="quality signature"
              />
            ) : null}
            <div>
              {!coa && (
                <FormattedMessage {...messages.CERTIFICATE_DISCLOSURE} />
              )}
            </div>
          </div>
        </Grid>
      </Paper>
    </Container>
  )
}

export default CertificateSharedLayout
